<template>
  <div>
    <v-toolbar
      flat
      color="white"
    >
      <v-toolbar-title data-cy="milestones-tab-title">Milestones</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-dialog
        v-model="dialog"
        max-width="500px"
      >
        <v-btn
          v-show="editMode"
          slot="activator"
          color="primary"
          dark
          class="mb-2 ml-5"
          data-cy="new-milestone-btn"
        >
          New Item
        </v-btn>
        <v-card>
          <v-card-title>
            <span class="headline">{{ formTitle }}</span>
          </v-card-title>
          <v-card-text>
            <v-container grid-list-xs>
              <v-layout wrap>
                <v-flex xs12>
                  <v-textarea
                    v-model="editedItem.milestone_desc"
                    :error-messages="textFieldErrors($v.editedItem.milestone_desc)"
                    :hint="`${editedItem.milestone_desc.length}/300 Characters - Description of your dataset.`"
                    label="Description"
                    @blur="$v.editedItem.milestone_desc.$touch()"
                    data-cy="edited-milestone-desc">
                  </v-textarea>
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                    type="number"
                    step="1"
                    v-model="editedItem.milestone_percent_complete"
                    :error-messages="percentageFieldErrors($v.editedItem.milestone_percent_complete)"
                    :hint="`0-100`"
                    suffix="%"
                    placeholder="0"
                    label="Percent Complete"
                    @input="$v.editedItem.milestone_percent_complete.$touch()"
                    @blur="$v.editedItem.milestone_percent_complete.$touch()"
                    data-cy="edited-milestone-percent">
                  </v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                    @click="showPlanDatePicker=(!showPlanDatePicker)"
                    v-model="editedItem.plan_date"
                    :error-messages="dateFieldErrors($v.editedItem.plan_date)"
                    :hint="`YYYY-MM-DD`"
                    label="Plan Date"
                    clearable
                    @input="$v.editedItem.actual_date.$touch()"
                    @blur="$v.editedItem.plan_date.$touch()"
                    data-cy="edited-milestone-plan-date">
                  </v-text-field>
                  <v-slide-y-transition>
                    <div v-show="showPlanDatePicker">
                      <v-date-picker
                        v-model="editedItem.plan_date"
                        landscape
                        reactive>
                      </v-date-picker>
                    </div>
                  </v-slide-y-transition>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="blue darken-1"
              flat
              @click.native="close"
            >
              Cancel
            </v-btn>
            <v-btn
              color="blue darken-1"
              flat
              @click.native="save"
              data-cy="milestone-save-btn"
              :disabled="$v.editedItem.$invalid"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <v-data-table
      :headers="currentHeaders"
      :items="dataset.dataset_milestone"
      hide-actions
      class="elevation-1"
      style="border-top-style: solid; border-width: 1px; border-color: #D3D3D3;"
      data-cy="milestone-table"
    >
      <template
        slot="items"
        slot-scope="props"
        :class="{ presentation: getPresentMode }"
      >
        <td
          :class="{ presentation: getPresentMode }">
          {{ props.item.milestone_desc }}
        </td>
        <td
          :class="{ presentation: getPresentMode }">
          {{ props.item.plan_date }}
        </td>
        <td
          :class="{ presentation: getPresentMode }">
          {{ props.item.milestone_percent_complete }}
        </td>
        <td
          v-if="editMode"
          class="justify-center mx-auto"
        >

        <font-awesome-icon
            size="lg"
            class="mx-1"
            :icon="['far', 'edit']"
            @click="editItem(props.item)"
          >
          </font-awesome-icon>
        <font-awesome-icon
            size="lg"
            class="mx-1"
            :icon="['far', 'trash']"
            @click="deleteItem(props.item)"
          >
          </font-awesome-icon>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { required, helpers, between, maxLength } from 'vuelidate/lib/validators'
import { validationMixin, noDecimal, date } from '../validators.js'

export default {
  mixins: [validationMixin],
  props: {
    editMode: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      editedIndex: -1,
      dialog: false,
      headers: [
        {
          text: 'Description',
          align: 'left',
          sortable: false,
          value: 'milestone_desc'
        },
        { text: 'Plan Date', value: 'plan_date' },
        { text: '%', value: 'milestone_percent_complete' },
        { text: 'Actions', value: 'name', sortable: false }
      ],
      editedItem: {
        milestone_desc: '',
        milestone_percent_complete: '',
        plan_date: '',
      },
      defaultItem: {
        milestone_desc: '',
        milestone_percent_complete: 0,
        plan_date: '',
      },
      editedKey: 0,
      showPlanDatePicker: false
    }
  },
  validations: {
    editedItem: {
      milestone_desc: {
        required,
        maxLength: maxLength(300)
      },
      milestone_percent_complete: {
        required,
        between: between(0, 100),
        noDecimal,
        maxLength: maxLength(3)
      },
      plan_date: {
        required,
        date,
        maxLength: maxLength(10)
      }
    }
  },
  computed: {
    ...mapGetters([
      'getCurrentDataset',
      'getPresentMode',
    ]),
    formTitle () {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item'
    },
    currentHeaders () {
      return this.editMode ? this.headers : this.headers.slice(0, -1)
    },
    dataset () {
      return this.getCurrentDataset
    },
  },
  watch: {
    dialog (val) {
      val || this.close()
    }
  },
  methods: {
    ...mapActions([
      'removeItemAction',
      'editItemAction',
      'addItemAction'
    ]),
    returnColor (item) {
        return ''
    },
    editItem (item) {
      this.editedIndex = this.dataset.dataset_milestone.indexOf(item)
      const keys = Object.keys(this.defaultItem)
      keys.forEach(key => { this.editedItem[key] = item[key] })
      this.editedKey = item.key
      this.dialog = true
    },
    deleteItem (item) {
      const payload = {
        array: 'dataset_milestone',
        endpoint: 'milestone',
        key: item.key,
        id: this.dataset.dataset_id
      }
      confirm('Are you sure you want to delete this item?') &&
      this.removeItemAction(payload)
    },
    close () {
      this.dialog = false
      setTimeout(() => {
        this.$v.$reset()
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      }, 300)
    },
    save () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        const payload = {
          array: 'dataset_milestone',
          endpoint: 'milestone',
          key: this.editedKey,
          id: this.dataset.dataset_id,
          data: { ...this.editedItem }
        }
        if (this.editedIndex > -1) {
          this.editItemAction(payload)
        } else {
          this.addItemAction(payload)
        }
        this.close()
      }
    }
  }

}
</script>
